import QRCode from 'qrcode';
import type { Session } from 'next-auth';
import type { IInvoice } from '@/types/invoice';
import { calculateInvoiceTotals } from './invoice-math';

// ─── Shared types ────────────────────────────────────────────────

export interface PaymentQrParams {
  bic: string;
  beneficiaryName: string;
  beneficiaryAddress?: string;
  beneficiaryCity?: string;
  iban: string;
  amount: number;
  currency: string;
  reference: string;
  country: string;
  dueDate?: Date;
  purposeText?: string;
  bankAccount?: string; // Serbian 18-digit bank account (non-IBAN)
}

export interface PaymentQrResult {
  dataUrl: string;
  label: string;
}

// ─── ISO-8859-2 encoding (browser-safe, no deps) ────────────────

const ISO_8859_2_MAP: Record<number, number> = {
  0x104: 0xa1,
  0x2d8: 0xa2,
  0x141: 0xa3,
  0xa4: 0xa4,
  0x13d: 0xa5,
  0x15a: 0xa6,
  0xa7: 0xa7,
  0xa8: 0xa8,
  0x160: 0xa9,
  0x15e: 0xaa,
  0x164: 0xab,
  0x179: 0xac,
  0xad: 0xad,
  0x17d: 0xae,
  0x17b: 0xaf,
  0xb0: 0xb0,
  0x105: 0xb1,
  0x2db: 0xb2,
  0x142: 0xb3,
  0xb4: 0xb4,
  0x13e: 0xb5,
  0x15b: 0xb6,
  0x2c7: 0xb7,
  0xb8: 0xb8,
  0x161: 0xb9,
  0x15f: 0xba,
  0x165: 0xbb,
  0x17a: 0xbc,
  0x2dd: 0xbd,
  0x17e: 0xbe,
  0x17c: 0xbf,
  0x154: 0xc0,
  0xc1: 0xc1,
  0xc2: 0xc2,
  0x102: 0xc3,
  0xc4: 0xc4,
  0x139: 0xc5,
  0x106: 0xc6,
  0xc7: 0xc7,
  0x10c: 0xc8,
  0xc9: 0xc9,
  0x118: 0xca,
  0xcb: 0xcb,
  0x11a: 0xcc,
  0xcd: 0xcd,
  0xce: 0xce,
  0x10e: 0xcf,
  0x110: 0xd0,
  0x143: 0xd1,
  0x147: 0xd2,
  0xd3: 0xd3,
  0xd4: 0xd4,
  0x150: 0xd5,
  0xd6: 0xd6,
  0xd7: 0xd7,
  0x158: 0xd8,
  0x16e: 0xd9,
  0xda: 0xda,
  0x170: 0xdb,
  0xdc: 0xdc,
  0xdd: 0xdd,
  0x162: 0xde,
  0xdf: 0xdf,
  0x155: 0xe0,
  0xe1: 0xe1,
  0xe2: 0xe2,
  0x103: 0xe3,
  0xe4: 0xe4,
  0x13a: 0xe5,
  0x107: 0xe6,
  0xe7: 0xe7,
  0x10d: 0xe8,
  0xe9: 0xe9,
  0x119: 0xea,
  0xeb: 0xeb,
  0x11b: 0xec,
  0xed: 0xed,
  0xee: 0xee,
  0x10f: 0xef,
  0x111: 0xf0,
  0x144: 0xf1,
  0x148: 0xf2,
  0xf3: 0xf3,
  0xf4: 0xf4,
  0x151: 0xf5,
  0xf6: 0xf6,
  0xf7: 0xf7,
  0x159: 0xf8,
  0x16f: 0xf9,
  0xfa: 0xfa,
  0x171: 0xfb,
  0xfc: 0xfc,
  0xfd: 0xfd,
  0x163: 0xfe,
  0x2d9: 0xff,
};

function encodeISO88592(str: string): Uint8Array {
  const bytes: number[] = [];
  for (let i = 0; i < str.length; i++) {
    const cp = str.codePointAt(i)!;
    if (cp <= 0x7f) {
      bytes.push(cp);
    } else if (cp <= 0xff && cp >= 0xa0) {
      bytes.push(ISO_8859_2_MAP[cp] ?? cp);
    } else if (ISO_8859_2_MAP[cp] !== undefined) {
      bytes.push(ISO_8859_2_MAP[cp]);
    } else {
      bytes.push(0x3f); // '?' for unmappable
    }
  }
  return new Uint8Array(bytes);
}

// ─── Country detection ──────────────────────────────────────────

type QrCountry = 'SI' | 'RS' | 'OTHER';

export function detectCountry(country: string): QrCountry {
  const c = country.toLowerCase().trim();
  if (c === 'slovenia' || c === 'slovenija' || c === 'si') return 'SI';
  if (c === 'serbia' || c === 'srbija' || c === 'rs') return 'RS';
  return 'OTHER';
}

// ─── UPN QR (Slovenian standard) ────────────────────────────────

function formatUpnDate(date: Date): string {
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

export function buildUpnQrPayload(params: PaymentQrParams): Uint8Array {
  const amount = String(Math.round(params.amount * 100)).padStart(11, '0');
  const dueDate = params.dueDate ? formatUpnDate(new Date(params.dueDate)) : '';

  const fields = [
    'UPNQR', // 1: header
    '', // 2: payer IBAN
    '', // 3: deposit
    '', // 4: withdrawal
    '', // 5: payer reference
    '', // 6: payer name
    '', // 7: payer street
    '', // 8: payer city
    amount, // 9: amount
    '', // 10: payment date
    '', // 11: urgent
    'GDSV', // 12: purpose code
    params.purposeText?.substring(0, 42) || params.reference, // 13: purpose text
    dueDate, // 14: due date
    params.iban.replace(/\s/g, '').toUpperCase(), // 15: recipient IBAN
    `SI00${params.reference}`.substring(0, 26), // 16: recipient reference
    params.beneficiaryName.substring(0, 33), // 17: recipient name
    (params.beneficiaryAddress || '').substring(0, 33), // 18: recipient street
    (params.beneficiaryCity || '').substring(0, 33), // 19: recipient city
  ];

  let payload = fields.join('\n') + '\n';
  const checksum = String(payload.length).padStart(3, '0');
  payload += checksum + '\n';

  while (payload.length < 411) {
    payload += ' ';
  }

  return encodeISO88592(payload);
}

async function generateUpnQr(params: PaymentQrParams): Promise<string> {
  const payloadBytes = buildUpnQrPayload(params);

  return QRCode.toDataURL([{ data: payloadBytes, mode: 'byte' as const }], {
    version: 15,
    errorCorrectionLevel: 'M',
    margin: 2,
    width: 200,
  });
}

// ─── IPS QR (Serbian NBS standard) ──────────────────────────────

function formatIpsAmount(amount: number, currency: string): string {
  const fixed = amount.toFixed(2);
  const withComma = fixed.replace('.', ',');
  return `${currency.toUpperCase()}${withComma}`;
}

export function normalizeSerbianAccount(iban: string): string {
  // If already 18 digits, use as-is
  const digits = iban.replace(/\D/g, '');
  if (digits.length === 18) return digits;
  // If RS IBAN (RS35 + 18 digits), extract the 18-digit account
  if (iban.toUpperCase().startsWith('RS') && digits.length >= 18) {
    return digits.slice(digits.length - 18);
  }
  return digits.padStart(18, '0');
}

export function buildIpsQrPayload(params: PaymentQrParams): string {
  const account = normalizeSerbianAccount(params.bankAccount || params.iban);
  const currency = params.currency?.toUpperCase() === 'RSD' ? 'RSD' : 'RSD';
  const amount = formatIpsAmount(params.amount, currency);
  const name = (params.beneficiaryName || '')
    .substring(0, 70)
    .replace(/\|/g, '-');
  const purpose = (params.purposeText || params.reference || '')
    .substring(0, 35)
    .replace(/\|/g, '-');
  const reference = `00${params.reference}`.substring(0, 35);

  const parts = [
    `K:PR`,
    `V:01`,
    `C:1`,
    `R:${account}`,
    `N:${name}`,
    `I:${amount}`,
    `SF:189`,
    `S:${purpose}`,
    `RO:${reference}`,
  ];

  return parts.join('|');
}

async function generateIpsQr(params: PaymentQrParams): Promise<string> {
  const payload = buildIpsQrPayload(params);

  return QRCode.toDataURL(payload, {
    errorCorrectionLevel: 'M',
    margin: 2,
    width: 200,
  });
}

// ─── EPC QR (SEPA GiroCode) ────────────────────────────────────

export function buildEpcQrString(params: PaymentQrParams): string {
  const lines = [
    'BCD',
    '002',
    '1',
    'SCT',
    params.bic.replace(/\s/g, '').toUpperCase(),
    params.beneficiaryName.substring(0, 70),
    params.iban.replace(/\s/g, '').toUpperCase(),
    `EUR${params.amount.toFixed(2)}`,
    '',
    params.reference.substring(0, 140),
  ];
  return lines.join('\n');
}

async function generateEpcQr(params: PaymentQrParams): Promise<string> {
  const payload = buildEpcQrString(params);
  return QRCode.toDataURL(payload, {
    width: 200,
    margin: 2,
    errorCorrectionLevel: 'M',
  });
}

// ─── Public API ────────────────────────────────────────────────

export function shouldShowPaymentQr(session: Session | null): boolean {
  return !!session?.user?.accountNumber && !!session?.user?.bic;
}

/**
 * Build the QR params object from a session + invoice. Used by both the
 * client-side download flow and the server-side render-for-email flow so
 * both surfaces produce identical QR codes.
 */
export function buildQrParamsFromSession(
  session: Session,
  invoice: IInvoice
): PaymentQrParams {
  const { total } = calculateInvoiceTotals(invoice.items);
  return {
    bic: session.user.bic!,
    beneficiaryName: session.user.companyName || '',
    beneficiaryAddress: session.user.companyAddress || '',
    beneficiaryCity: session.user.city
      ? `${session.user.zipCode || ''} ${session.user.city}`.trim()
      : '',
    iban: session.user.accountNumber!,
    amount: total,
    currency: session.user.currency || 'EUR',
    reference: invoice.invoiceNumber,
    country: session.user.country || '',
    dueDate: invoice.dueDate,
    purposeText: `${invoice.invoiceType} ${invoice.invoiceNumber}`,
  };
}

export async function generatePaymentQrCodes(
  params: PaymentQrParams
): Promise<PaymentQrResult[]> {
  if (
    !params.beneficiaryName ||
    !params.iban ||
    !params.amount ||
    params.amount <= 0
  ) {
    return [];
  }

  const qrCountry = detectCountry(params.country);
  const results: PaymentQrResult[] = [];

  if (qrCountry === 'SI') {
    results.push({
      dataUrl: await generateUpnQr(params),
      label: 'UPN QR',
    });
  } else if (qrCountry === 'RS') {
    // Serbian banks (IPS QR)
    results.push({
      dataUrl: await generateIpsQr(params),
      label: 'NBS IPS QR',
    });
  } else {
    // All other EU countries (EPC/GiroCode)
    if (params.bic) {
      results.push({
        dataUrl: await generateEpcQr(params),
        label: 'EPC QR',
      });
    }
  }

  return results;
}
